<script setup>
import { computed, ref } from 'vue';
import BaseBubble from 'next/message/bubbles/Base.vue';
import FormattedContent from './FormattedContent.vue';
import AttachmentChips from 'next/message/chips/AttachmentChips.vue';
import TranslationToggle from 'dashboard/components-next/message/TranslationToggle.vue';
import ComposeConversation from 'dashboard/components-next/NewConversation/ComposeConversation.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import { MESSAGE_TYPES } from '../../constants';
import { useMessageContext } from '../../provider.js';
import { useTranslations } from 'dashboard/composables/useTranslations';
import { extractPhoneActions } from '../../helpers/phoneActionHelper';

const {
  content,
  attachments,
  contentAttributes,
  inboxId,
  messageType,
  orientation,
} = useMessageContext();

const { hasTranslations, translationContent } =
  useTranslations(contentAttributes);

const renderOriginal = ref(false);

const renderContent = computed(() => {
  if (renderOriginal.value) {
    return content.value;
  }

  if (hasTranslations.value) {
    return translationContent.value;
  }

  return content.value;
});

const phoneActions = computed(() => {
  if (
    contentAttributes.value?.deleted ||
    contentAttributes.value?.deletedByContact
  ) {
    return { sharedContact: null, phones: [] };
  }

  return extractPhoneActions(content.value);
});

const sharedContact = computed(() => phoneActions.value.sharedContact);
const inlinePhones = computed(() => phoneActions.value.phones);
const composeAlign = computed(() =>
  orientation.value === 'right' ? 'end' : 'start'
);
const sharedContactLabel = 'Contato compartilhado';

const isTemplate = computed(() => {
  return messageType.value === MESSAGE_TYPES.TEMPLATE;
});

const isEmpty = computed(() => {
  return !content.value && !attachments.value?.length;
});

const handleSeeOriginal = () => {
  renderOriginal.value = !renderOriginal.value;
};
</script>

<template>
  <BaseBubble class="px-4 py-3" data-bubble-name="text">
    <div class="gap-3 flex flex-col">
      <span v-if="isEmpty" class="text-n-slate-11">
        {{ $t('CONVERSATION.NO_CONTENT') }}
      </span>

      <div v-if="sharedContact" class="flex min-w-64 max-w-full flex-col gap-3">
        <div class="flex min-w-0 items-center gap-3">
          <div
            class="grid size-10 shrink-0 place-content-center rounded-lg bg-n-alpha-3"
          >
            <span class="i-lucide-contact-round size-5" />
          </div>
          <div class="flex min-w-0 flex-1 flex-col">
            <span class="text-xs text-n-slate-11">
              {{ sharedContactLabel }}
            </span>
            <strong class="truncate text-sm text-n-slate-12">
              {{ sharedContact.name }}
            </strong>
            <span class="truncate text-sm text-n-slate-11">
              {{ sharedContact.label }}
            </span>
          </div>
        </div>

        <ComposeConversation
          :initial-phone="sharedContact.phone"
          :initial-name="sharedContact.name"
          :initial-inbox-id="inboxId"
          :align="composeAlign"
        >
          <template #trigger="{ isOpen }">
            <NextButton
              class="w-full skip-context-menu"
              label="Enviar mensagem"
              icon="i-lucide-message-circle"
              color="teal"
              size="sm"
              :aria-expanded="isOpen"
            />
          </template>
        </ComposeConversation>
      </div>

      <FormattedContent v-else-if="renderContent" :content="renderContent" />

      <TranslationToggle
        v-if="hasTranslations && !sharedContact"
        class="-mt-3"
        :showing-original="renderOriginal"
        @toggle="handleSeeOriginal"
      />

      <div
        v-if="inlinePhones.length"
        class="flex flex-wrap gap-1.5 border-t border-n-weak pt-2"
      >
        <ComposeConversation
          v-for="phone in inlinePhones"
          :key="phone.phone"
          :initial-phone="phone.phone"
          :initial-inbox-id="inboxId"
          :align="composeAlign"
        >
          <template #trigger="{ isOpen }">
            <NextButton
              v-tooltip.top="'Enviar mensagem para este numero'"
              class="skip-context-menu"
              :label="phone.label"
              icon="i-lucide-message-circle"
              variant="ghost"
              color="teal"
              size="xs"
              :aria-expanded="isOpen"
            />
          </template>
        </ComposeConversation>
      </div>

      <AttachmentChips :attachments="attachments" class="gap-2" />
      <template v-if="isTemplate">
        <div
          v-if="contentAttributes.submittedEmail"
          class="px-2 py-1 rounded-lg bg-n-alpha-3"
        >
          {{ contentAttributes.submittedEmail }}
        </div>
      </template>
    </div>
  </BaseBubble>
</template>

<style>
p:last-child {
  margin-bottom: 0;
}
</style>
